import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Video, Search, Sparkles, ClipboardCheck, FileVideo, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/teacher/live/library")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Recording Library — MAKAI" }] }),
  component: TeacherLibraryPage,
});

type Row = {
  id: string; title: string; description: string | null;
  scheduled_start: string; actual_end: string | null; status: string;
  ai_summary: string | null; homework: string | null;
  recording_resource_id: string | null; recording_storage_path: string | null;
  stream_id: string;
};

function TeacherLibraryPage() {
  const ctx = useTeacherContext();
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!ctx.staffId) return;
    (async () => {
      const { data } = await supabase.from("live_lessons")
        .select("id, title, description, scheduled_start, actual_end, status, ai_summary, homework, recording_resource_id, recording_storage_path, stream_id")
        .eq("teacher_id", ctx.staffId!)
        .in("status", ["ended", "live"])
        .order("scheduled_start", { ascending: false })
        .limit(100);
      setRows((data ?? []) as Row[]);
    })();
  }, [ctx.staffId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.title.toLowerCase().includes(needle) || (r.ai_summary?.toLowerCase().includes(needle) ?? false),
    );
  }, [rows, q]);

  return (
    <AppShell role="teacher" title="Recording library" subtitle="Past lessons, AI summaries and homework"
      action={<Button asChild variant="ghost" size="sm"><Link to="/teacher/live"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link></Button>}>
      <Card className="mb-4 flex items-center gap-2 p-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input className="border-0 shadow-none focus-visible:ring-0" placeholder="Search by title or summary…" value={q} onChange={(e) => setQ(e.target.value)} />
      </Card>

      {filtered.length === 0 ? (
        <Card className="grid place-items-center p-10 text-center text-sm text-muted-foreground">
          <FileVideo className="mb-3 h-10 w-10 opacity-40" />
          No past lessons yet. Host a live class and it will appear here.
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((r) => (
            <Card key={r.id} className="flex flex-col gap-2 p-4">
              <div className="min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="truncate font-semibold">{r.title}</h3>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {r.status}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{new Date(r.scheduled_start).toLocaleString()}</p>
              </div>
              {r.ai_summary && (
                <p className="line-clamp-3 text-sm text-foreground/80">
                  <Sparkles className="-mt-1 mr-1 inline h-3.5 w-3.5 text-primary" />
                  {r.ai_summary}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button asChild size="sm" className="gap-1">
                  <Link to="/teacher/live/$id" params={{ id: r.id }}><Video className="h-4 w-4" /> Open</Link>
                </Button>
                {r.recording_storage_path && (
                  <Button size="sm" variant="outline" className="gap-1" onClick={async () => {
                    const { signedUrl } = await import("@/lib/uploads");
                    const url = await signedUrl("library-files", r.recording_storage_path!, 3600);
                    if (url) window.open(url, "_blank", "noopener");
                  }}>
                    <FileVideo className="h-4 w-4" /> Recording
                  </Button>
                )}
                {r.homework && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                    <ClipboardCheck className="h-3 w-3" /> Homework
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
